import type { OnboardingStep } from "@/services/types";

export const ONBOARDING_QUERY_KEY = ["onboarding"] as const;
// Read through an optional chain so importing this module does not require Vite to have defined
// import.meta.env. Vite still inlines the value at build time; the guard only matters outside the
// bundler, where a bare dereference threw at import and made the module untestable.
export const FIRST_TIME_ONBOARDING_ENABLED =
  import.meta.env?.VITE_FIRST_TIME_ONBOARDING_ENABLED !== "false";

export const ONBOARDING_STEPS: OnboardingStep[] = [
  "PURPOSE",
  "AVAILABILITY",
  // Calendar authorization is its own step now. It MUST be listed here: parseOnboardingStep
  // validates against this array, so without it "?step=calendar" is silently discarded — which
  // also breaks the return trip from the provider's OAuth redirect.
  "CALENDAR",
  "FIRST_EVENT",
  "SUCCESS",
];

export function onboardingPath(step: OnboardingStep) {
  return `/onboarding?step=${step.toLowerCase()}`;
}

export function parseOnboardingStep(raw: string | null): OnboardingStep | null {
  const value = String(raw ?? "").toUpperCase();
  return ONBOARDING_STEPS.includes(value as OnboardingStep) ? value as OnboardingStep : null;
}

/**
 * Every step a user could walk through. SUCCESS is excluded: it is the confirmation screen, not a
 * step the user works through.
 *
 * Derived from ONBOARDING_STEPS rather than written as a number. The indicator used to hardcode
 * three dots and clamp its label to "step 3 of 3"; when CALENDAR was added as its own step the
 * count was missed, so both CALENDAR and FIRST_EVENT rendered a full bar.
 */
const ALL_PROGRESS_STEPS = ONBOARDING_STEPS.filter((step) => step !== "SUCCESS");

/**
 * The steps THIS user actually walks through.
 *
 * Calendar access now arrives with the sign-in authorization itself, so the CALENDAR step is
 * skipped outright for most new hosts — both `continueAvailability` and the server's `resumeStep`
 * route straight from availability to the first event. Counting a step nobody will see made the
 * bar promise four and deliver three, so a step appeared to vanish between availability and the
 * booking link.
 *
 * It stays counted for anyone who still has to connect: someone who declined calendar access, or
 * whose grant could not be turned into a stored connection.
 */
export function onboardingProgressSteps(calendarReady: boolean) {
  return calendarReady
    ? ALL_PROGRESS_STEPS.filter((step) => step !== "CALENDAR")
    : ALL_PROGRESS_STEPS;
}
